import React, { useEffect, useRef, useState } from 'react';
import { receiveMessage } from '../websocket.slice';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { IReceivedMessage, IUserSettingsAddPathBody, IUserSettingsAddProjectPathBody, TWebsocketFilepath, TWebsocketHostname, TWebsocketProjectpath, TWebsocketScriptSettings } from '../../../../interface/interfaces';
import { triggerAnythingFunc, triggerScriptFunc } from '../../../../slices/triggerAnything';
import { isTWebsocketFilepath, isTWebsocketHostname, isTWebsocketProjectpath, isTWebsocketScriptSettings } from '../utils/checkObjectProperty';
import { saveHostnameAndUsername } from '../websocketHostname.slice';
import { useAppSelector } from '../../../../redux/hooks';
import { selectorWebsocket } from '../redux/selector';
import { fetchUserSettingsFilePathPending } from '../../UserSettings/FilePath/userSettingsAddFilePath.slice';
import { addToAllPaths } from '../../UserSettings/FilePath/userSettingsFilePathFastChanging.slice';
import { closeModal, openModal } from '../../Modal/modal.slice';
import { fetchUserSettingsAddProjectPathPending } from '../../UserSettings/ProjectPath/userSettingsAddProjectPath.slice';
import { saveProjectPath } from '../../UserSettings/ProjectPath/userSettingsProjectPathFastChanging.slice';
import { saveScriptSettings } from '../../Datamanager/Inspector/ScriptSettings/scriptSettings.slice';
import { text } from 'stream/consumers';
import { saveScriptUpdateWsData } from '../../Datamanager/Inspector/ScriptSettings/scriptUpdate.slice';

export const useWebSocket = (section: string) => {
    const dispatch = useDispatch();
    const socketRef = useRef<WebSocket | null>(null);

    const {loginUserAndTokenData, project, hostAndUsername, savedUserToken, userSettingsFilePath, userSettingsAddProjectPath} = useAppSelector(selectorWebsocket)
    const [isReady, setIsReady] = useState(false);

    // const {receivedMessageFeature} = useAppSelector((state:rootState)=>state.webSocket)

    const isArrayOfAnything = (input: unknown): input is any[] => {
        return Array.isArray(input);
      };

    const handleTextObject = (websocketResponseData: IReceivedMessage) => {

        const textObject = JSON.parse(websocketResponseData.text)

        // check if it is hostname
        if(isTWebsocketHostname(textObject)){
            dispatch(saveHostnameAndUsername(textObject as TWebsocketHostname));
            toast.success(`Hostname ${textObject.osHostName} added.`);
            return;
        }

        // check if it for file path
        if (isTWebsocketFilepath(textObject as TWebsocketFilepath)){
            const body:IUserSettingsAddPathBody = {
                user_id: loginUserAndTokenData.userAndTokenData?.user.uniqueId || '',
                project_id: project.project?._id || '',
                device_id: `${hostAndUsername.hostnameAndUsername?.osHostName}_${hostAndUsername.hostnameAndUsername?.osUserName}`,
                tool: textObject.tool,
                file_path: textObject.filePath,
            }

            if(!userSettingsFilePath.userSettingsFilePathError && !userSettingsFilePath.userSettingsFilePathLoading){
                dispatch(addToAllPaths(body));
                toast.success(`Filepath ${(textObject as TWebsocketFilepath).filePath} added.`);
            }
            // adding file path to db
            dispatch(fetchUserSettingsFilePathPending({url:`${process.env.REACT_APP_USER_SETTINGS_API}/file-path`, token:`${savedUserToken.savedUserToken}`, body}))
            return;
        }

        if (isTWebsocketProjectpath(textObject as TWebsocketProjectpath)){
            const body:IUserSettingsAddProjectPathBody = {
                user_id: loginUserAndTokenData.userAndTokenData?.user.uniqueId || '',
                project_id: project.project?._id || '',
                device_id: `${hostAndUsername.hostnameAndUsername?.osHostName}_${hostAndUsername.hostnameAndUsername?.osUserName}`,
                project_path: textObject.projectPath,
            }

            if(!userSettingsAddProjectPath.userSettingsAddProjectPathError && !userSettingsAddProjectPath.userSettingsAddProjectPathLoading){
                dispatch(saveProjectPath(body));
                toast.success(`Project path ${(textObject as TWebsocketProjectpath).projectPath} added.`);
            }
            // adding project path to db
            dispatch(fetchUserSettingsAddProjectPathPending({url:`${process.env.REACT_APP_USER_SETTINGS_API}/project-path`, token:`${savedUserToken.savedUserToken}`, body}))
            return;
        }

        //check if it is script settings
        if (isArrayOfAnything(textObject) && textObject.length > 0 && isTWebsocketScriptSettings(textObject[0] as TWebsocketScriptSettings)){
            dispatch(saveScriptUpdateWsData(textObject as TWebsocketScriptSettings[]));
            toast.success(`Getting scripts version successful.`);
            return;
        }
    
        toast.success(websocketResponseData.text);
    }

    useEffect(() => {
        socketRef.current = new WebSocket(`${process.env.REACT_APP_WEBSOCKET}`);

        socketRef.current.onopen = () => {
            // eslint-disable-next-line no-console
            setIsReady(true);
            console.log(`Connected to WebSocket: ${section}`);
        };

        socketRef.current.onclose = () => {
            setIsReady(false);
            console.log(`WebSocket closed: ${section}`);
        };
        
        socketRef.current.onerror = (error) => {
            setIsReady(false);
            console.error(`WebSocket error: ${section}`, error);
        };

        socketRef.current.onmessage = (event: { data: string }) => {        
            // dispatch(fetchScriptUpdateWsPending)    
            try {
                // JSON parse websocket response from backend
                const websocketResponseData = JSON.parse(event.data) as IReceivedMessage;
                // check if parsing is valid
                if (websocketResponseData) {
                    // add response message to redux state
                    dispatch(receiveMessage(websocketResponseData));
                    // check if is message is loading
                    if (websocketResponseData.isLoading) {
                        // if loading is true open modal with websocketResponseData.text
                        dispatch(openModal("websocketProcessModal"));
                    } else {
                        // HERE TRIGGER COMMIT FETCH AND CUSTOM NODE (FIX)
                        // stop fetch department commit and departmnent status on geting script version and fetch everything else
                        // trigger when update is finish
                        if(websocketResponseData.text==='Features have finished downloading'){
                            dispatch(triggerScriptFunc());
                        }
                        dispatch(triggerAnythingFunc());
                        dispatch(closeModal("websocketProcessModal"));
                        
                        handleTextObject(websocketResponseData);
                        
                           
                    }
                }
            } catch (err) {
                // dispatch(receiveMessage(event.data));
                // if event.data is string (if parsing is not valid)
                const parsedMessage = JSON.parse(event.data) as IReceivedMessage;
                toast.success(parsedMessage.text || 'Default message.');
            }

        };

        return () => {
            socketRef.current?.close();
            // eslint-disable-next-line no-console
            setIsReady(false);
            console.log(`Disconnected from Websocket: ${section}`);
        };
    }, [dispatch]);

    return { socketRef, isReady  };
};
